import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { SIGNALR_HUB_URL } from '../config';

// Singleton instance cho connection và callbacks
let globalConnection = null;
let connectionPromise = null;
const callbacks = new Set();
const notificationCallbacks = new Set();

const handleDataChanged = (entity, action, data) => {
    callbacks.forEach(cb => {
        try {
            cb(entity, action, data);
        } catch (e) {
            console.error("[SignalR Web] Error executing callback: ", e);
        }
    });
};

const handleReceiveNotification = (notification) => {
    notificationCallbacks.forEach(cb => {
        try {
            cb(notification);
        } catch (e) {
            console.error("[SignalR Web] Error executing notification callback: ", e);
        }
    });
};

export const useSignalR = (onDataChanged, onReceiveNotification) => {
    const callbackRef = useRef(onDataChanged);
    const notifCallbackRef = useRef(onReceiveNotification);

    // Luôn giữ callback mới nhất
    useEffect(() => {
        callbackRef.current = onDataChanged;
    }, [onDataChanged]);

    useEffect(() => {
        notifCallbackRef.current = onReceiveNotification;
    }, [onReceiveNotification]);

    useEffect(() => {
        const currentCallback = (entity, action, data) => {
            if (callbackRef.current) {
                callbackRef.current(entity, action, data);
            }
        };

        const currentNotifCallback = (notification) => {
            if (notifCallbackRef.current) {
                notifCallbackRef.current(notification);
            }
        };

        if (onDataChanged) {
            callbacks.add(currentCallback);
        }

        if (onReceiveNotification) {
            notificationCallbacks.add(currentNotifCallback);
        }
        
        const startConnection = async () => {
            if (globalConnection && globalConnection.state === signalR.HubConnectionState.Connected) {
                return;
            }
            
            if (connectionPromise) {
                try {
                    await connectionPromise;
                    return;
                } catch (e) {}
            }

            const token = localStorage.getItem('token');
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(SIGNALR_HUB_URL, {
                    accessTokenFactory: () => token || ""
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            connection.on("DataChanged", handleDataChanged);
            connection.on("ReceiveNotification", handleReceiveNotification);

            connectionPromise = connection.start()
                .then(() => {
                    globalConnection = connection;
                    connectionPromise = null;
                })
                .catch(err => {
                    connectionPromise = null;
                    console.error("[SignalR Web] Connection failed: ", err);
                    // Thử lại sau 5s nếu lỗi
                    setTimeout(startConnection, 5000);
                });
                
            await connectionPromise;
        };

        startConnection();

        return () => {
            if (onDataChanged) {
                callbacks.delete(currentCallback);
            }
            if (onReceiveNotification) {
                notificationCallbacks.delete(currentNotifCallback);
            }
            
            // Trì hoãn ngắt kết nối để xem có component nào khác mount ngay sau đó không
            setTimeout(() => {
                if (callbacks.size === 0 && notificationCallbacks.size === 0 && globalConnection) {
                    const connToStop = globalConnection;
                    globalConnection = null;
                    connToStop.stop()
                        .then(() => {})
                        .catch(err => console.error("[SignalR Web] Error stopping connection: ", err));
                }
            }, 1000);
        };
    }, [onDataChanged, onReceiveNotification]);

    return {
        connection: globalConnection
    };
};
